import React from 'react';
import {
  PencilSquareIcon,
  BanknotesIcon,
  DocumentTextIcon,
  UserPlusIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import type { Client } from '../../types';
import { deriveClientStatus } from '../clients/clientStatus';
import { ClientStatusBadge } from '../clients/ClientStatusBadge';
import { ClientPlanChip } from '../clients/ClientPlanChip';
import { applyDominicanIDMask } from '../../utils/validation';
import { profileAvatarClass, profileInitials } from './clientProfileUtils';

export interface ClientProfileHeaderProps {
  client: Client;
  variant?: 'modal' | 'page';
  onEdit?: () => void;
  onRegisterPayment?: () => void;
  onViewInvoices?: () => void;
  onAddFamily?: () => void;
  onViewHistory?: () => void;
  /** Cierra modal y navega a /clients/:id */
  onViewFullDetail?: () => void;
  className?: string;
}

const btnBase =
  'inline-flex items-center justify-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1';

export const ClientProfileHeader: React.FC<ClientProfileHeaderProps> = ({
  client,
  variant = 'page',
  onEdit,
  onRegisterPayment,
  onViewInvoices,
  onAddFamily,
  onViewHistory,
  onViewFullDetail,
  className
}) => {
  const status = deriveClientStatus(client);
  const cedula =
    applyDominicanIDMask(String(client.cedula || '').replace(/\D/g, '')) || client.cedula;
  const registered = client.createdAt
    ? new Date(client.createdAt).toLocaleDateString('es', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      })
    : '—';

  const isCompact = variant === 'modal';

  return (
    <div
      className={clsx(
        'rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-slate-50/40 to-white p-5 shadow-sm ring-1 ring-slate-100/80',
        className
      )}
    >
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 flex-1 gap-4">
          <div
            className={clsx(
              'flex shrink-0 items-center justify-center rounded-2xl font-bold text-slate-900 ring-2 ring-white',
              isCompact ? 'h-14 w-14 text-lg' : 'h-16 w-16 text-xl',
              profileAvatarClass(`${client.name}${client.lastname}${client.id}`)
            )}
          >
            {profileInitials(client.name, client.lastname)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2
                className={clsx(
                  'font-bold tracking-tight text-slate-900',
                  isCompact ? 'text-lg sm:text-xl' : 'text-xl sm:text-2xl'
                )}
              >
                {client.name} {client.lastname}
              </h2>
              <ClientStatusBadge status={status} />
            </div>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-1">
              <p className="text-sm text-slate-600">
                <span className="font-medium text-slate-500">Documento:</span>{' '}
                <span className="font-mono tabular-nums text-slate-900">{cedula}</span>
              </p>
              <p className="text-sm text-slate-600">
                <span className="font-medium text-slate-500">Registro:</span>{' '}
                <span className="text-slate-900">{registered}</span>
              </p>
            </div>
            <div className="mt-3 max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Plan actual</p>
              <div className="mt-1">
                <ClientPlanChip client={client} />
              </div>
            </div>
          </div>
        </div>

        <div
          className={clsx(
            'flex flex-col gap-2',
            isCompact ? 'w-full lg:w-auto lg:min-w-[min(100%,280px)]' : 'w-full lg:w-auto lg:max-w-md'
          )}
        >
          {onViewFullDetail && (
            <button
              type="button"
              onClick={onViewFullDetail}
              className={clsx(
                btnBase,
                'w-full border-slate-900 bg-slate-900 text-white shadow-md shadow-slate-900/15 hover:bg-slate-800'
              )}
            >
              <ArrowTopRightOnSquareIcon className="h-5 w-5 shrink-0" />
              Ver más detalles
            </button>
          )}
          <div
            className={clsx(
              'grid gap-2',
              isCompact ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 xl:grid-cols-3'
            )}
          >
            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className={clsx(btnBase, 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50')}
              >
                <PencilSquareIcon className="h-4 w-4" />
                Editar
              </button>
            )}
            {onRegisterPayment && (
              <button
                type="button"
                onClick={onRegisterPayment}
                className={clsx(btnBase, 'border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100')}
              >
                <BanknotesIcon className="h-4 w-4" />
                Pago
              </button>
            )}
            {onViewInvoices && (
              <button
                type="button"
                onClick={onViewInvoices}
                className={clsx(btnBase, 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50')}
              >
                <DocumentTextIcon className="h-4 w-4" />
                Facturas
              </button>
            )}
            {onAddFamily && (
              <button
                type="button"
                onClick={onAddFamily}
                className={clsx(btnBase, 'border-indigo-200 bg-indigo-50 text-indigo-900 hover:bg-indigo-100')}
              >
                <UserPlusIcon className="h-4 w-4" />
                Familiar
              </button>
            )}
            {onViewHistory && (
              <button
                type="button"
                onClick={onViewHistory}
                className={clsx(btnBase, 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50')}
              >
                <ClockIcon className="h-4 w-4" />
                Historial
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
